/* eslint-disable */
// Paste support for the one-row-per-outlet-item order sheet:
//
//   Product Full Name     | Unit Of Measure | Ordered Quantity | Internal Order Number | Internal Order From Location | ...
//   Boiled Penne Pasta (CK) | portion       | 58               | IO151682              | BLR-HSR-01                   |
//   Boiled Penne Pasta (CK) | portion       | 38               | IO151684              | BLR-BELLA-02                 |
//
// The table paste expects one row per item with one column per outlet, so this pivots the sheet
// into that shape (the outlet column's values become the headers, quantities of the same item and
// outlet are summed) and the normal paste runs on the result. Unit and date columns are ignored:
// the quantity is taken as it is and the form's own dates stay.

const normHeader = (h = '') => String(h ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

const ITEM_HEADERS = ['product full name', 'product name', 'item name', 'item', 'product'];
const QTY_HEADERS = ['ordered quantity', 'order quantity', 'ordered qty', 'order qty', 'quantity', 'qty'];
const LOCATION_HEADERS = [
	'internal order from location', 'order from location', 'from location',
	'outlet', 'outlet name', 'store', 'store name', 'location',
];

// Exact header names first; then the first header passing `loose` (sheets add suffixes such as
// "Ordered Quantity (UOM)" or "From Location Code").
const findHeader = (headers, names, loose) => {
	const normalized = headers.map(normHeader);
	for (const name of names) {
		const index = normalized.indexOf(name);
		if (index !== -1) return headers[index];
	}
	const index = normalized.findIndex(loose);
	return index === -1 ? null : headers[index];
};

const looseItem = (h) => (h.includes('product') || h.includes('item')) && h.includes('name');
const looseQty = (h) => /\b(qty|quantity)\b/.test(h) && !h.includes('unit');
const looseLocation = (h) => /\b(location|outlet)\b/.test(h);

const ITEM_NAME = 'Item Name';

const parseQty = (value) => {
	const n = parseFloat(String(value ?? '').replace(/[,\s]/g, ''));
	return Number.isFinite(n) ? n : null;
};

// The columns of the sheet, or null when the paste is not in this format.
export const findLongFormatColumns = (headers = []) => {
	const itemCol = findHeader(headers, ITEM_HEADERS, looseItem);
	const qtyCol = findHeader(headers, QTY_HEADERS, looseQty);
	const locationCol = findHeader(headers, LOCATION_HEADERS, looseLocation);
	if (!itemCol || !qtyCol || !locationCol) return null;
	return { itemCol, qtyCol, locationCol };
};

// parsed = parseTSVData output ({ headers, rows: [{ header: value }] }).
// tableHeaders = the table's columns, to report outlets that match no column.
// Returns { headers, rows, unmatchedLocations } in the parseTSVData shape, with the item column
// renamed to 'Item Name' so findItemNameColumn always finds it.
export const pivotLongFormat = (parsed, columns, tableHeaders = []) => {
	const { itemCol, qtyCol, locationCol } = columns;
	const locations = [];
	const byItem = {};
	const itemOrder = [];
	parsed.rows.forEach((row) => {
		const item = String(row[itemCol] ?? '').trim();
		const location = String(row[locationCol] ?? '').trim();
		const qty = parseQty(row[qtyCol]);
		if (!item || !location || qty === null) return;
		if (!locations.includes(location)) locations.push(location);
		if (!byItem[item]) {
			byItem[item] = {};
			itemOrder.push(item);
		}
		byItem[item][location] = (byItem[item][location] ?? 0) + qty;
	});

	const rows = itemOrder.map((item) => {
		const row = { [ITEM_NAME]: item };
		locations.forEach((location) => {
			const qty = byItem[item][location];
			// Round away float noise from summing (0.1 + 0.2).
			row[location] = qty === undefined ? '' : String(Math.round(qty * 1000) / 1000);
		});
		return row;
	});

	const columnNames = new Set();
	tableHeaders.forEach((h) => {
		if (h?.storeName) columnNames.add(String(h.storeName).toLowerCase());
		if (h?.shortName) columnNames.add(String(h.shortName).toLowerCase());
	});
	const unmatchedLocations = locations.filter((l) => !columnNames.has(l.toLowerCase()));

	return { headers: [ITEM_NAME, ...locations], rows, unmatchedLocations };
};
